/**
 * Le souvenir de l'invitation : une clé, une seule, définitive.
 *
 * # Pourquoi elle s'écrit à l'affichage et non à la réponse
 *
 * Le cas le plus probable n'est ni « elle répond » ni « elle refuse », c'est
 * « elle ferme l'onglet pendant que la capsule est à l'écran ». Écrire au
 * moment où l'invitation devient visible couvre les trois d'un coup : dès
 * qu'elle a été vue, elle a été posée, et la question ne se repose pas.
 *
 * Les issues suivantes réécrivent la clé pour la mesure, mais aucune ne change
 * la décision : c'est la *présence* de la clé qui bloque, pas sa valeur.
 *
 * # Pourquoi le navigateur, et pas la session ni le compte
 *
 * La session vit en mémoire — `sessionId` est régénéré à chaque chargement —
 * donc une règle de portée session redemanderait à chaque rechargement, ce qui
 * est précisément le harcèlement qu'on veut éviter. Le compte, lui, n'existe
 * pas : la session est anonyme par défaut, une règle par compte ne protégerait
 * personne.
 *
 * # Pourquoi l'échec se referme
 *
 * `whats-new-context` échoue en ouvert et l'assume : au pire la popin se
 * rouvre, ce qui est sans conséquence. Ici c'en a une. Une invitation qui ne
 * peut pas mémoriser son refus revient à chaque session, indéfiniment. Perdre
 * une occasion vaut mieux que redemander sans fin, donc un stockage
 * indisponible se lit comme « déjà montrée ».
 */

const STORAGE_KEY = "virtuosa.feedback.prompt";

/** Ce qu'est devenue l'invitation. Conservé pour la relecture, pas pour la décision. */
export type PromptOutcome = "shown" | "answered" | "dismissed";

type StoredPrompt = {
  readonly v: 1;
  readonly state: PromptOutcome;
  readonly at: number;
};

/**
 * L'invitation a-t-elle déjà eu lieu sur ce navigateur ?
 *
 * Rend `true` — donc « ne montre pas » — quand le stockage est inaccessible,
 * pour la raison dite plus haut.
 */
export function hasPromptBeenShown(): boolean {
  if (typeof localStorage === "undefined") {
    return true;
  }

  try {
    return localStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return true;
  }
}

/**
 * Consigne le sort de l'invitation.
 *
 * Silencieux en cas d'échec : l'appelant n'a rien à faire de plus, et la garde
 * de {@link hasPromptBeenShown} refermera de toute façon la porte au prochain
 * chargement.
 */
export function recordPromptOutcome(state: PromptOutcome): void {
  const payload: StoredPrompt = { v: 1, state, at: Date.now() };

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Stockage plein, refusé, ou navigation privée : rien à rattraper ici.
  }
}
